import type { Tool } from '@xsai/shared-chat'

import type { ContextMessage } from '../types/chat'

import { ContextUpdateStrategy } from '@proj-airi/server-sdk'
import { rawTool } from '@xsai/tool'

import { frameEvidence } from './velis-evidence'

export type OperationEffect = 'read' | 'write' | 'external-action'
export type OperationSource = 'first-party' | 'mcp'
export type OperationRisk = 'read' | 'low-risk-write' | 'external'
export type ApprovalPolicy = 'allow' | 'explicit-intent' | 'approval'
export type RuntimeProfile = 'desktop' | 'web'
export type OperationAvailability = 'available' | 'unavailable'

/** Selection metadata for one model-callable action. The tool schema stays on the tool. */
export interface OperationDescriptor {
  id: string
  domain: string
  source: OperationSource
  effect: OperationEffect
  risk: OperationRisk
  approval: ApprovalPolicy
  profiles: readonly RuntimeProfile[]
  timeoutMs: number
  maxResultChars: number
  mcpName?: string
}

export interface ContextProviderDescriptor {
  id: 'memory' | 'task' | 'session-time' | 'held-context'
  priority: number
  budgetChars: number
  freshnessMs: number
}

export interface OperationRuntimeState {
  id: string
  availability: OperationAvailability
  unavailableReason?: string
}

export interface ProviderUse {
  id: ContextProviderDescriptor['id']
  included: boolean
  chars: number
}

export interface UnavailableOperation {
  id: string
  reason: 'desktop-only'
}

export interface ExposureDecision {
  candidateDomains: string[]
  operations: OperationDescriptor[]
  unavailable: UnavailableOperation[]
  reason: string
}

export type PolicyStatus = 'allowed' | 'pending-confirmation' | 'rejected'

export interface PolicyDecision {
  allowed: boolean
  status: PolicyStatus
  reason: string
}

export interface CapabilityResultTrace {
  operationId: string
  status: string
  evidence: string
  rawChars: number
  compactedChars: number
  latencyMs: number
}

export interface CapabilityTrace {
  providers: ProviderUse[]
  candidateDomains: string[]
  operations: Array<{
    id: string
    source: OperationSource
    effect: OperationEffect
    risk: OperationRisk
    approval: string
  }>
  unavailable?: UnavailableOperation[]
  result?: CapabilityResultTrace
}

export interface McpOperationSource {
  serverName: string
  name: string
  toolName: string
  description?: string
  inputSchema: Record<string, unknown>
}

const governedNames = new Set([
  'search_public',
  'list_reminders',
  'create_reminder',
  'complete_reminder',
  'save_local_note',
  'save_reminder',
  'read_held_context',
  'builtIn_mcpListTools',
  'builtIn_mcpCallTool',
])

/**
 * Context providers that enter the prompt before any Operation.
 * Memory, task, and session time stay out of the tool list.
 */
export const contextProviders: readonly ContextProviderDescriptor[] = [
  { id: 'memory', priority: 10, budgetChars: 1200, freshnessMs: 60_000 },
  { id: 'task', priority: 20, budgetChars: 400, freshnessMs: 60_000 },
  { id: 'session-time', priority: 30, budgetChars: 160, freshnessMs: 60_000 },
  { id: 'held-context', priority: 40, budgetChars: 800, freshnessMs: 120_000 },
]

/**
 * First-party operations for V2.1.
 * `read` and `write` are effects. They are not tool names.
 */
export function firstPartyOperations(): OperationDescriptor[] {
  return [
    operation('search_public', 'search', 'read', 'allow', 10_000, 1200, ['desktop', 'web']),
    operation('list_reminders', 'reminder', 'read', 'allow', 5_000, 800),
    operation('create_reminder', 'reminder', 'write', 'explicit-intent', 5_000, 800),
    operation('complete_reminder', 'reminder', 'write', 'explicit-intent', 5_000, 800),
    operation('save_local_note', 'note', 'write', 'explicit-intent', 10_000, 800),
  ]
}

/**
 * True when this name is selected by the capability catalog.
 * History alone cannot put it back into the next request.
 */
export function isGovernedOperationName(name: string) {
  return governedNames.has(name) || name.startsWith('weather__') || name.startsWith('spotify__')
}

/**
 * Chooses the operations for one user message.
 * An earlier tool call does not keep an operation visible.
 *
 * @example
 * exposeOperations({ text: '你好', profile: 'desktop', operations: firstPartyOperations(), runtime: [] })
 * // => { operations: [], candidateDomains: [], reason: 'no-domain' }
 */
export function exposeOperations(input: {
  text: string
  profile: RuntimeProfile
  operations: readonly OperationDescriptor[]
  runtime: readonly OperationRuntimeState[]
  pendingApprovalIds?: readonly string[]
  continuationIds?: readonly string[]
}): ExposureDecision {
  const available = availableOperations(input)
  const domains = domainsFor(input.text)
  const selected = new Map<string, OperationDescriptor>()

  for (const domain of domains) {
    for (const operation of operationsForDomain(domain, input.text, available))
      selected.set(operation.id, operation)
  }

  if (isContinuation(input.text)) {
    for (const id of input.continuationIds ?? []) {
      const operation = available.find(item => item.id === id)
      if (operation)
        selected.set(operation.id, operation)
    }
  }

  if (isConfirmation(input.text)) {
    for (const id of input.pendingApprovalIds ?? []) {
      const operation = available.find(item => item.id === id)
      if (operation)
        selected.set(operation.id, operation)
    }
  }

  const operations = [...selected.values()]
  const unavailable = desktopOnlyOperations(input, domains)
  return {
    candidateDomains: domains,
    operations,
    unavailable,
    reason: operations.length === 0 ? (unavailable.length > 0 ? 'desktop-only' : 'no-domain') : 'matched',
  }
}

/** Names operations the current profile cannot run, so the model states that outcome. */
export function unavailableOperationSupplement(text: string, profile: RuntimeProfile) {
  const unavailable = exposeOperations({
    text,
    profile,
    operations: firstPartyOperations(),
    runtime: [],
  }).unavailable
  if (unavailable.length === 0)
    return ''
  return unavailable.map(item => `${item.id} is ${item.reason}.`).join('\n')
}

/** Web reads this. Electron stays desktop when the environment flag is absent, including unit tests. */
export function runtimeProfile(): RuntimeProfile {
  return import.meta.env.RUNTIME_ENVIRONMENT === 'browser' ? 'web' : 'desktop'
}

/**
 * Decides whether one exposed operation may run.
 * The model cannot grant a write by setting `confirmed`.
 *
 * @example
 * decideOperationPolicy({ operation: firstPartyOperations()[2]!, userText: '你好', approval: 'none', blocked: [], exposedIds: ['create_reminder'] })
 * // => { allowed: false, status: 'pending-confirmation', reason: 'write-not-requested' }
 */
export function decideOperationPolicy(input: {
  operation: OperationDescriptor
  userText: string
  approval: 'none' | 'granted' | 'rejected'
  blocked: readonly { domain: string, effect: OperationEffect }[]
  exposedIds: readonly string[]
}): PolicyDecision {
  const blocked = input.blocked.some(item => item.domain === input.operation.domain && item.effect === input.operation.effect)
  if (input.approval === 'rejected' || blocked) {
    return {
      allowed: false,
      status: 'rejected',
      reason: 'approval-rejected',
    }
  }

  if (!input.exposedIds.includes(input.operation.id)) {
    return { allowed: false, status: 'rejected', reason: 'not-exposed' }
  }

  if (input.operation.effect === 'read')
    return { allowed: true, status: 'allowed', reason: 'read' }

  if (input.operation.effect === 'external-action') {
    if (input.approval === 'granted')
      return { allowed: true, status: 'allowed', reason: 'approval-granted' }
    return { allowed: false, status: 'pending-confirmation', reason: 'external-action' }
  }

  if (input.approval === 'granted' || userRequestedWrite(input.operation.id, input.userText))
    return { allowed: true, status: 'allowed', reason: 'explicit-intent' }

  return { allowed: false, status: 'pending-confirmation', reason: 'write-not-requested' }
}

/**
 * Keeps the status and a short evidence line when a result is large.
 * The model receives the compact JSON. The trace keeps both sizes.
 *
 * @example
 * compactOperationResult({ status: 'ok', evidence: 'Velis', extra: 'x'.repeat(50) }, 40)
 */
export function compactOperationResult(value: unknown, maxChars: number): {
  value: unknown
  evidence: string
  rawChars: number
  compactedChars: number
} {
  const raw = JSON.stringify(value) ?? ''
  const evidence = evidenceFrom(value)
  if (raw.length <= maxChars) {
    return { value, evidence, rawChars: raw.length, compactedChars: raw.length }
  }

  const compact = {
    status: statusFrom(value),
    evidence: evidence.slice(0, 180),
    omittedChars: raw.length,
  }
  const text = JSON.stringify(compact)
  return {
    value: compact,
    evidence: compact.evidence,
    rawChars: raw.length,
    compactedChars: text.length,
  }
}

/**
 * Reads one MCP tool list into catalog rows.
 * Only the weather server enters V2.1. Other servers stay on the developer MCP path.
 *
 * @example
 * operationsFromMcpTools([{ serverName: 'weather', name: 'weather::get_forecast', toolName: 'get_forecast', inputSchema: { type: 'object', properties: {} } }])
 */
export function operationsFromMcpTools(tools: readonly McpOperationSource[]): {
  operations: OperationDescriptor[]
  runtime: OperationRuntimeState[]
} {
  const operations: OperationDescriptor[] = []
  const runtime: OperationRuntimeState[] = []
  for (const tool of tools) {
    const domain = domainForMcpServer(tool.serverName)
    if (!domain)
      continue
    const effect = effectForMcpTool(tool.toolName)
    const id = modelNameForMcp(domain, tool.toolName)
    operations.push({
      id,
      domain,
      source: 'mcp',
      effect,
      risk: effect === 'read' ? 'read' : 'external',
      approval: effect === 'read' ? 'allow' : 'approval',
      profiles: ['desktop'],
      timeoutMs: 10_000,
      maxResultChars: 1200,
      mcpName: tool.name,
    })
    runtime.push({ id, availability: 'available' })
  }
  return { operations, runtime }
}

/** Builds one xsai tool that calls an MCP operation by its qualified name. */
export function createMcpOperationTool(
  source: McpOperationSource,
  operation: OperationDescriptor,
  call: (payload: { name: string, arguments?: Record<string, unknown> }) => Promise<unknown>,
): Tool {
  return rawTool({
    name: operation.id,
    description: source.description?.trim() || `${operation.domain} ${source.toolName}`,
    parameters: objectSchema(source.inputSchema),
    execute: async (input) => {
      const args = typeof input === 'object' && input !== null && !Array.isArray(input)
        ? input as Record<string, unknown>
        : {}
      return await call({ name: source.name, arguments: args })
    },
  })
}

/** Writes the current UTC time into the session-time context slot. */
export function createSessionTimeContext(now: number): ContextMessage {
  return {
    id: 'velis:session-time:context',
    contextId: 'velis:session-time',
    strategy: ContextUpdateStrategy.ReplaceSelf,
    metadata: { source: { id: 'velis:session-time' } },
    text: frameEvidence('session-time', new Date(now).toISOString()),
    createdAt: now,
  }
}

/** Writes one active task into its own context slot. */
export function createTaskContext(text: string, now = Date.now()): ContextMessage | undefined {
  const body = text.trim().slice(0, 400)
  if (!body)
    return undefined
  return {
    id: 'velis:task:context',
    contextId: 'velis:task',
    strategy: ContextUpdateStrategy.ReplaceSelf,
    metadata: { source: { id: 'velis:task' } },
    text: frameEvidence('task', body),
    createdAt: now,
  }
}

/**
 * Renders one capability trace for the workbench.
 *
 * @example
 * formatCapabilityTrace({ providers: [{ id: 'session-time', included: true, chars: 20 }], candidateDomains: [], operations: [] })
 * // => 'providers session-time; domains none; operations none; approval none; result none'
 */
export function formatCapabilityTrace(trace: CapabilityTrace) {
  const providers = trace.providers.filter(item => item.included).map(item => item.id).join(',') || 'none'
  const domains = trace.candidateDomains.join(',') || 'none'
  const operations = trace.operations.map(item => `${item.id} ${item.source} ${item.effect} ${item.risk}`).join(',') || 'none'
  const approval = trace.operations.map(item => `${item.id}:${item.approval}`).join(',') || 'none'
  const result = trace.result
    ? `${trace.result.operationId} ${trace.result.status} evidence=${trace.result.evidence} raw=${trace.result.rawChars} compact=${trace.result.compactedChars} latency=${trace.result.latencyMs}`
    : 'none'
  const unavailable = trace.unavailable?.map(item => `${item.id}:${item.reason}`).join(',') || 'none'
  return `providers ${providers}; domains ${domains}; operations ${operations}; approval ${approval}; unavailable ${unavailable}; result ${result}`
}

/**
 * Launch row for the weather MCP server.
 * `ENABLED_TOOLS=basic` keeps the six read tools proved on 2026-10-05.
 * Spotify stays out until this read path is stable.
 */
export const weatherMcpLaunch = {
  serverName: 'weather',
  command: 'npx',
  args: ['-y', '@dangahagan/weather-mcp@latest'],
  env: {
    ENABLED_TOOLS: 'basic',
  },
} as const

function operation(
  id: string,
  domain: string,
  effect: OperationEffect,
  approval: ApprovalPolicy,
  timeoutMs: number,
  maxResultChars: number,
  profiles: readonly RuntimeProfile[] = ['desktop'],
): OperationDescriptor {
  return {
    id,
    domain,
    source: 'first-party',
    effect,
    risk: effect === 'write' ? 'low-risk-write' : 'read',
    approval,
    profiles,
    timeoutMs,
    maxResultChars,
  }
}

function desktopOnlyOperations(input: {
  profile: RuntimeProfile
  operations: readonly OperationDescriptor[]
}, domains: readonly string[]): UnavailableOperation[] {
  return input.operations.flatMap((item) => {
    if (!domains.includes(item.domain) || item.profiles.includes(input.profile))
      return []
    return [{ id: item.id, reason: 'desktop-only' as const }]
  })
}

function availableOperations(input: {
  profile: RuntimeProfile
  operations: readonly OperationDescriptor[]
  runtime: readonly OperationRuntimeState[]
}) {
  const runtime = new Map(input.runtime.map(item => [item.id, item]))
  return input.operations.filter((item) => {
    if (!item.profiles.includes(input.profile))
      return false
    const state = runtime.get(item.id)
    if (!state)
      return item.source === 'first-party'
    return state.availability === 'available'
  })
}

function domainsFor(text: string) {
  const domains: string[] = []
  if (wantsWeather(text))
    domains.push('weather')
  if (wantsSearch(text) && !wantsWeather(text))
    domains.push('search')
  if (wantsCreateReminder(text) || wantsCompleteReminder(text) || wantsListReminder(text))
    domains.push('reminder')
  if (wantsNote(text))
    domains.push('note')
  return domains
}

function operationsForDomain(domain: string, text: string, available: readonly OperationDescriptor[]) {
  if (domain === 'search')
    return available.filter(item => item.id === 'search_public')
  if (domain === 'note')
    return available.filter(item => item.id === 'save_local_note')
  if (domain === 'reminder') {
    if (wantsCreateReminder(text))
      return available.filter(item => item.id === 'create_reminder')
    if (wantsCompleteReminder(text))
      return available.filter(item => item.id === 'complete_reminder')
    return available.filter(item => item.id === 'list_reminders')
  }
  if (domain === 'weather')
    return relevantWeatherReads(text, available)
  return []
}

function wantsWeather(text: string) {
  if (!/天气|气温|下雨|降雨|预报|空气质量|weather|forecast|\brain\b|temperature|air quality/i.test(text))
    return false
  return /[?？]|吗|会不会|查|多少|怎么样|\bwhat\b|\bwhen\b|\bwill\b|\bhow\b/i.test(text)
}

function wantsSearch(text: string) {
  return /查一下|搜一下|搜索|资料|wikipedia|search for|look up/i.test(text)
}

function wantsCreateReminder(text: string) {
  return /提醒我|remind me|创建提醒|设(?:一个|个)?提醒|添加提醒/i.test(text)
}

function wantsCompleteReminder(text: string) {
  return /完成提醒|complete(?: the)? reminder|把提醒.{0,24}完成|标成完成/i.test(text)
}

function wantsListReminder(text: string) {
  if (wantsCreateReminder(text) || wantsCompleteReminder(text))
    return false
  if (/别提醒|不要提醒|don't remind/i.test(text))
    return false
  return /提醒|reminder/i.test(text)
}

function wantsNote(text: string) {
  return /保存笔记|记一条笔记|本地笔记|save (?:a )?note/i.test(text)
}

function userRequestedWrite(operationId: string, text: string) {
  if (operationId === 'create_reminder')
    return wantsCreateReminder(text)
  if (operationId === 'complete_reminder')
    return wantsCompleteReminder(text)
  if (operationId === 'save_local_note')
    return wantsNote(text)
  return false
}

function isContinuation(text: string) {
  return /继续刚才|继续查|continue that|same search/i.test(text)
}

function isConfirmation(text: string) {
  return /确认|^\s*confirm\b/i.test(text)
}

function domainForMcpServer(serverName: string) {
  const name = serverName.trim().toLowerCase()
  if (name === 'weather' || name.startsWith('weather-') || name.startsWith('weather_'))
    return 'weather'
  return undefined
}

function effectForMcpTool(toolName: string): OperationEffect {
  if (/play|pause|skip|delete|remove|create|write|post|send|control|save_/i.test(toolName))
    return 'external-action'
  return 'read'
}

function relevantWeatherReads(text: string, available: readonly OperationDescriptor[]) {
  const reads = available.filter(item => item.domain === 'weather' && item.effect === 'read')
  return reads.filter(item => weatherToolMatches(text, item.id))
}

function weatherToolMatches(text: string, id: string) {
  const name = id.toLowerCase()
  const forecastBlocked = /alert|lightning|location|wildfire|marine|river|service_status|imagery/.test(name)
  if (/空气|aqi|air quality|pollen/i.test(text))
    return /air|pollen/.test(name)
  if (/闪电|lightning/i.test(text))
    return /lightning/.test(name)
  if (/海浪|marine|\bwave\b/i.test(text))
    return /marine/.test(name)
  if (/山火|wildfire/i.test(text))
    return /wildfire/.test(name)
  if (/河流|flood|river/i.test(text))
    return /river/.test(name)
  if (/下雨|预报|forecast|rain|今晚|明天/i.test(text))
    return !forecastBlocked && /forecast|summary/.test(name)
  return !forecastBlocked && /summary|forecast|current/.test(name)
}

function modelNameForMcp(domain: string, toolName: string) {
  const safe = toolName.replace(/[^a-zA-Z0-9_-]/g, '_')
  return `${domain}__${safe}`
}

function objectSchema(schema: Record<string, unknown>) {
  if (schema.type === 'object')
    return schema
  return { type: 'object', properties: {}, additionalProperties: false }
}

function statusFrom(value: unknown) {
  if (typeof value === 'object' && value && 'status' in value && typeof value.status === 'string')
    return value.status
  if (typeof value === 'object' && value && 'isError' in value && value.isError === true)
    return 'failed'
  return 'ok'
}

function evidenceFrom(value: unknown) {
  if (typeof value !== 'object' || value === null)
    return ''
  const record = value as {
    evidence?: unknown
    message?: unknown
    reminder?: { id?: unknown }
    hits?: Array<{ title?: unknown }>
    content?: Array<{ text?: unknown }>
  }
  if (typeof record.evidence === 'string')
    return record.evidence
  if (typeof record.reminder?.id === 'string')
    return record.reminder.id
  const title = record.hits?.[0]?.title
  if (typeof title === 'string')
    return title
  const text = record.content?.find(part => typeof part.text === 'string')?.text
  if (typeof text === 'string')
    return boundedEvidence(text)
  if (typeof record.message === 'string')
    return record.message.slice(0, 180)
  return ''
}

/** Keeps rain and temperature lines when a forecast body is longer than the model budget. */
function boundedEvidence(text: string) {
  const lines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0)
  const useful = lines.filter(line => /rain|precip|下雨|降雨|\bhigh\b|\blow\b|°f|°c/i.test(line))
  const chosen = useful.length > 0 ? useful.slice(0, 3).join(' ') : text
  return chosen.replace(/\s+/g, ' ').trim().slice(0, 180)
}
